/**
 * BranchSelector — toolbar dropdown for switching and creating branches.
 *
 * Pinned "main" at the top, feature branches below, "New Branch" at bottom.
 */

import { useState } from "react";
import { Badge, Button, DropdownMenu, Flex, Text } from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useBranches, useDeleteBranch } from "../../hooks/useVersioning";
import { useAnonymous } from "../../context/AnonymousContext";
import { NewBranchDialog } from "./NewBranchDialog";
import { RenameBranchDialog } from "./RenameBranchDialog";

function GitBranchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
      <path d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.5 2.5 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Zm-6 0a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Zm8.25-.75a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5ZM4.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z" />
    </svg>
  );
}

export function BranchSelector() {
  const { projectId, currentBranch, setBranch } = useProvenanceContext();
  const { data: branches } = useBranches(projectId);
  const { isAnonymous } = useAnonymous();
  const deleteMutation = useDeleteBranch(projectId);
  const [newBranchOpen, setNewBranchOpen] = useState(false);
  const [renameBranch, setRenameBranch] = useState<string | null>(null);

  const isMain = currentBranch === "main";

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <Button variant="soft" size="1" color={isMain ? "blue" : "orange"}>
            <GitBranchIcon />
            {currentBranch}
            <DropdownMenu.TriggerIcon />
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content>
          <DropdownMenu.Label>Branches</DropdownMenu.Label>

          <DropdownMenu.Item
            onSelect={() => setBranch("main")}
          >
            <Flex justify="between" align="center" width="100%" gap="3">
              <Text>main</Text>
              <Badge size="1" color="blue">default</Badge>
            </Flex>
          </DropdownMenu.Item>

          {branches
            ?.filter((b) => b.name !== "main")
            .map((b) => (
              <DropdownMenu.Item
                key={b.id}
                onSelect={() => setBranch(b.name)}
              >
                <Flex justify="between" align="center" width="100%" gap="3">
                  <Text>{b.name}</Text>
                  {!isAnonymous && (
                    <Flex gap="1">
                      <Button
                        variant="ghost"
                        size="1"
                        color="gray"
                        title="Rename branch"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenameBranch(b.name);
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 15 15" fill="currentColor">
                          <path d="M11.854 1.146a.5.5 0 0 0-.708 0L3.714 8.578a1 1 0 0 0-.258.44l-.718 2.514a.5.5 0 0 0 .616.616l2.514-.718a1 1 0 0 0 .44-.258l7.432-7.432a.5.5 0 0 0 0-.708l-1.886-1.886ZM4.421 9.285l6.726-6.726 1.293 1.293-6.726 6.726-1.775.507.482-1.8Z" />
                        </svg>
                      </Button>
                      {currentBranch !== b.name && (
                        <Button
                          variant="ghost"
                          size="1"
                          color="red"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm(`Delete branch "${b.name}"?`)) {
                              deleteMutation.mutate(b.name);
                              if (currentBranch === b.name) setBranch("main");
                            }
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 15 15" fill="currentColor">
                            <path d="M5.5 1a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1h-4ZM3 3.5a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1H11v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4h-.5a.5.5 0 0 1-.5-.5Z" />
                          </svg>
                        </Button>
                      )}
                    </Flex>
                  )}
                </Flex>
              </DropdownMenu.Item>
            ))}

          <DropdownMenu.Separator />

          {!isAnonymous && (
            <DropdownMenu.Item onSelect={() => setNewBranchOpen(true)}>
              + New Branch
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      <NewBranchDialog
        open={newBranchOpen}
        onOpenChange={setNewBranchOpen}
      />

      {renameBranch && (
        <RenameBranchDialog
          open={!!renameBranch}
          onOpenChange={(open) => { if (!open) setRenameBranch(null); }}
          currentName={renameBranch}
        />
      )}
    </>
  );
}
